// ✅ src/screens/WithdrawScreen.js (FULL)
// Ghana Cedis (GHS) wallet + withdrawals (NO FEE)
// - Auto creates wallet doc
// - Shows balance + pending (GHS)
// - Submit: balance -> pending (GHS) + create withdrawals doc (REQUESTED)
// ✅ Withdrawal History (user sees their past requests + status)

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  doc,
  onSnapshot,
  runTransaction,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
} from "firebase/firestore";
import { auth, db } from "../lib/firebase";

/* ---------------- helpers ---------------- */
function safeStr(v) {
  return String(v ?? "").trim();
}
function num(v) {
  // ✅ allow "11,00" or "1,234.50"
  const raw = String(v ?? "").replace(/,/g, "").trim();
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}
function msFromTs(ts) {
  try {
    if (!ts) return null;
    if (typeof ts === "number") return ts;
    if (ts?.toMillis) return ts.toMillis();
    if (ts?.toDate) return ts.toDate().getTime();
    return null;
  } catch {
    return null;
  }
}
function fmtDate(ts) {
  const ms = msFromTs(ts);
  if (!ms) return "";
  const d = new Date(ms);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}
function maskAccount(s) {
  const t = safeStr(s);
  if (!t) return "";
  if (t.length <= 4) return t;
  return `${t.slice(0, 2)}•••••${t.slice(-2)}`;
}
function statusLabel(s) {
  const t = safeStr(s).toUpperCase();
  if (!t) return "UNKNOWN";
  return t;
}
function ghsFmt(n) {
  const x = num(n);
  return `GHS ${x.toFixed(2)}`;
}

/* ---------------- wallet (auto-create) ---------------- */
async function ensureWallet(uid) {
  const ref = doc(db, "wallets", uid);

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) {
      tx.set(ref, {
        uid,
        balance: 0, // ✅ GHS balance
        pending: 0, // ✅ GHS pending (requested withdrawals)
        totalWithdrawn: 0, // ✅ GHS withdrawn (paid)
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        currency: "GHS",
      });
    }
  });

  return ref;
}

export default function WithdrawScreen() {
  const nav = useNavigate();

  const [me, setMe] = useState(null);

  // ✅ wallet values (GHS)
  const [balanceGhs, setBalanceGhs] = useState(0);
  const [pendingGhs, setPendingGhs] = useState(0);
  const [walletReady, setWalletReady] = useState(false);

  // ✅ history
  const [history, setHistory] = useState([]);
  const [historyReady, setHistoryReady] = useState(false);

  // form
  const [method, setMethod] = useState("momo");
  const [network, setNetwork] = useState("MTN");
  const [account, setAccount] = useState("");
  const [name, setName] = useState("");
  const [amountGhs, setAmountGhs] = useState(""); // user enters GHS

  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  // auth
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setMe(u || null));
    return () => unsub();
  }, []);

  // wallet listener (auto-create)
  useEffect(() => {
    if (!me?.uid) return;

    let unsub = null;

    (async () => {
      try {
        setWalletReady(false);
        await ensureWallet(me.uid);

        const wRef = doc(db, "wallets", me.uid);
        unsub = onSnapshot(
          wRef,
          (snap) => {
            const d = snap.exists() ? snap.data() || {} : {};
            setBalanceGhs(num(d.balance));
            setPendingGhs(num(d.pending));
            setWalletReady(true);
          },
          (e) => {
            console.error(e);
            setWalletReady(true);
          }
        );
      } catch (e) {
        console.error(e);
        setWalletReady(true);
      }
    })();

    return () => unsub && unsub();
  }, [me?.uid]);

  // withdrawals history listener
  useEffect(() => {
    if (!me?.uid) return;

    setHistoryReady(false);

    const qy = query(
      collection(db, "withdrawals"),
      where("uid", "==", me.uid),
      orderBy("createdAt", "desc"),
      limit(30)
    );

    const unsub = onSnapshot(
      qy,
      (snap) => {
        const rows = [];
        snap.forEach((d) => rows.push({ id: d.id, ...(d.data() || {}) }));
        setHistory(rows);
        setHistoryReady(true);
      },
      (e) => {
        console.error(e);
        setHistoryReady(true);
      }
    );

    return () => unsub();
  }, [me?.uid]);

  // ✅ NO FEE: user receives full amount
  const netGhs = useMemo(() => {
    const a = num(amountGhs);
    if (!Number.isFinite(a) || a <= 0) return 0;
    return Number(a.toFixed(2));
  }, [amountGhs]);

  async function submit() {
    setErr("");

    if (!me?.uid) return setErr("Please sign in first.");
    if (!safeStr(account)) return setErr("Enter your number/account.");
    if (method === "momo" && !safeStr(network)) return setErr("Pick a network.");

    const aGhs = num(amountGhs);
    if (!Number.isFinite(aGhs) || aGhs <= 0) return setErr("Enter a valid amount in GHS.");
    if (aGhs < 5) return setErr("Minimum withdraw is GHS 5.00.");

    // must have enough balance
    if (aGhs > balanceGhs) return setErr("Not enough balance.");

    setSubmitting(true);

    try {
      const withdrawalRef = doc(collection(db, "withdrawals"));
      const walletRef = doc(db, "wallets", me.uid);

      await runTransaction(db, async (tx) => {
        // ✅ READ wallet first
        const wSnap = await tx.get(walletRef);
        const w = wSnap.exists() ? wSnap.data() || {} : {};
        const bal = num(w.balance);
        const pend = num(w.pending);

        if (bal < aGhs) throw new Error("Not enough balance.");

        // ✅ UPDATE wallet: balance -> pending (GHS)
        tx.set(
          walletRef,
          {
            uid: me.uid,
            balance: Number((bal - aGhs).toFixed(2)),
            pending: Number((pend + aGhs).toFixed(2)),
            updatedAt: serverTimestamp(),
            currency: "GHS",
          },
          { merge: true }
        );

        // ✅ CREATE withdrawal request (NO FEE)
        // ✅ IMPORTANT: write BOTH (amount/fee/net) AND (amountGhs/feeGhs/netGhs)
        // so Admin screen never shows 0
        tx.set(withdrawalRef, {
          uid: me.uid,
          status: "REQUESTED",

          method,
          momoNetwork: method === "momo" ? network : "",
          momoNumber: safeStr(account),
          momoName: safeStr(name),

          // ✅ admin-compatible fields
          amount: Number(aGhs.toFixed(2)),
          fee: 0,
          net: Number(netGhs.toFixed(2)),

          // ✅ your GHS fields
          amountGhs: Number(aGhs.toFixed(2)),
          feeGhs: 0,
          netGhs: Number(netGhs.toFixed(2)),
          currency: "GHS",

          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        // (optional) wallet tx record
        const txRef = doc(collection(db, "wallets", me.uid, "transactions"));
        tx.set(txRef, {
          type: "WITHDRAW_REQUESTED",
          direction: "DEBIT",
          amount: Number(aGhs.toFixed(2)),
          fee: 0,
          net: Number(netGhs.toFixed(2)),
          amountGhs: Number(aGhs.toFixed(2)),
          feeGhs: 0,
          netGhs: Number(netGhs.toFixed(2)),
          status: "REQUESTED",
          refId: withdrawalRef.id,
          currency: "GHS",
          createdAt: serverTimestamp(),
        });
      });

      alert(`Withdraw request submitted ✅\n\nAmount: ${ghsFmt(aGhs)}\nYou receive: ${ghsFmt(netGhs)}`);

      setAmountGhs("");
      setAccount("");
      setName("");
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Failed to submit withdraw.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <button type="button" onClick={() => nav("/settings")} style={S.backBtn} aria-label="Back">
          ←
        </button>

        <div style={{ minWidth: 0 }}>
          <div style={S.h1}>Withdraw</div>
          <div style={S.h2}>Request a payout in Ghana cedis (GHS).</div>
        </div>

        <div style={S.walletCard} title="Your wallet">
          <div style={S.walletTop}>
            <span style={S.walletDot} />
            <span style={S.walletTitle}>Wallet (GHS)</span>
          </div>
          <div style={S.walletGrid}>
            <div style={S.walletStat}>
              <div style={S.walletLabel}>Balance</div>
              <div style={S.walletValue}>{walletReady ? ghsFmt(balanceGhs) : "…"}</div>
            </div>
            <div style={S.walletStat}>
              <div style={S.walletLabel}>Pending</div>
              <div style={S.walletValue}>{walletReady ? ghsFmt(pendingGhs) : "…"}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Content grid */}
      <div style={S.grid}>
        {/* LEFT: Form card */}
        <div style={S.card}>
          {err ? <div style={S.errBox}>{err}</div> : null}

          {/* Method */}
          <div style={S.block}>
            <div style={S.label}>Method</div>
            <div style={S.segment}>
              <button
                type="button"
                onClick={() => setMethod("momo")}
                style={{ ...S.segBtn, ...(method === "momo" ? S.segOn : null) }}
              >
                📱 Mobile Money
              </button>
              <button
                type="button"
                onClick={() => setMethod("bank")}
                style={{ ...S.segBtn, ...(method === "bank" ? S.segOn : null) }}
              >
                🏦 Bank
              </button>
            </div>
          </div>

          {/* Network */}
          {method === "momo" && (
            <div style={S.block}>
              <div style={S.label}>Network</div>
              <div style={S.field}>
                <select value={network} onChange={(e) => setNetwork(e.target.value)} style={S.select}>
                  <option value="MTN">MTN</option>
                  <option value="Telecel">Telecel</option>
                  <option value="AirtelTigo">AirtelTigo</option>
                </select>
              </div>
            </div>
          )}

          {/* Account */}
          <div style={S.block}>
            <div style={S.label}>{method === "momo" ? "MoMo number" : "Bank account number"}</div>
            <div style={S.field}>
              <input
                value={account}
                onChange={(e) => setAccount(e.target.value)}
                placeholder={method === "momo" ? "e.g. 055xxxxxxx" : "e.g. 0123456789"}
                style={S.input}
              />
            </div>
          </div>

          {/* Name */}
          <div style={S.block}>
            <div style={S.label}>Account name (optional)</div>
            <div style={S.field}>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Kel" style={S.input} />
            </div>
          </div>

          {/* Amount */}
          <div style={S.block}>
            <div style={S.label}>Amount (GHS)</div>
            <div style={S.field}>
              <input
                value={amountGhs}
                onChange={(e) => setAmountGhs(e.target.value.replace(/,/g, "").trim())}
                placeholder="e.g. 50"
                inputMode="decimal"
                style={S.input}
              />
            </div>
          </div>

          {/* Summary (NO FEE) */}
          <div style={S.summary}>
            <div style={S.sumRow}>
              <span style={S.sumKey}>You receive</span>
              <span style={{ ...S.sumVal, fontWeight: 1000 }}>{ghsFmt(netGhs)}</span>
            </div>
            <div style={{ opacity: 0.7, fontWeight: 800, fontSize: 12 }}>
              No fees — the receiver gets the full amount.
            </div>
          </div>

          <button
            type="button"
            style={{ ...S.cta, ...(submitting ? S.ctaOff : null) }}
            onClick={submit}
            disabled={submitting}
          >
            {submitting ? "Submitting..." : "Request withdraw"}
          </button>

          <div style={S.note}>
            This will show in Admin Payments under <b>REQUESTED</b>.
            <br />
            When you mark <b>PAID</b>, it should remove the amount from <b>pending</b>.
          </div>
        </div>

        {/* RIGHT: History card */}
        <div style={S.card}>
          <div style={S.cardHead}>
            <div>
              <div style={S.cardTitle}>Withdrawal history</div>
              <div style={S.cardSub}>Your latest 30 requests & statuses.</div>
            </div>
          </div>

          {!historyReady ? (
            <div style={S.empty}>Loading…</div>
          ) : history.length === 0 ? (
            <div style={S.empty}>No withdrawals yet.</div>
          ) : (
            <div style={S.list}>
              {history.map((w) => {
                const st = statusLabel(w.status);
                const methodLabel = safeStr(w.method) === "bank" ? "🏦 Bank" : "📱 MoMo";
                const networkLabel = safeStr(w.momoNetwork);
                const accountLabel = maskAccount(w.momoNumber);

                const amt =
                  Number.isFinite(num(w.amountGhs)) && num(w.amountGhs) > 0
                    ? num(w.amountGhs)
                    : num(w.amount);

                const net =
                  Number.isFinite(num(w.netGhs)) && num(w.netGhs) > 0 ? num(w.netGhs) : amt;

                const when = fmtDate(w.createdAt);

                return (
                  <div key={w.id} style={S.item}>
                    <div style={S.itemTop}>
                      <span style={S.statusPill(st)}>{st}</span>
                      <span style={S.meta}>
                        {methodLabel}
                        {safeStr(w.method) === "momo" && networkLabel ? ` • ${networkLabel}` : ""}
                        {accountLabel ? ` • ${accountLabel}` : ""}
                      </span>
                    </div>

                    <div style={S.itemBottom}>
                      <span style={S.amount}>
                        {ghsFmt(amt)} <span style={{ opacity: 0.5 }}>→</span> {ghsFmt(net)}
                      </span>
                      <span style={S.date}>{when}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- styles ---------------- */
const S = {
  page: { padding: 18, maxWidth: 980, margin: "0 auto", display: "grid", gap: 14 },

  header: {
    display: "flex",
    gap: 12,
    alignItems: "center",
    flexWrap: "wrap",
    padding: 14,
    borderRadius: 20,
    border: "1px solid rgba(2,6,23,0.08)",
    background: "linear-gradient(180deg, rgba(255,255,255,0.92), rgba(255,255,255,0.75))",
    boxShadow: "0 16px 40px rgba(2,6,23,0.06)",
  },
  backBtn: {
    width: 44,
    height: 44,
    borderRadius: 14,
    border: "1px solid rgba(2,6,23,0.12)",
    background: "white",
    cursor: "pointer",
    fontWeight: 1000,
    flexShrink: 0,
  },
  h1: { fontSize: 22, fontWeight: 1000, letterSpacing: -0.2 },
  h2: { opacity: 0.7, fontWeight: 800, fontSize: 12, marginTop: 2 },

  walletCard: {
    marginLeft: "auto",
    minWidth: 240,
    borderRadius: 18,
    border: "1px solid rgba(2,6,23,0.10)",
    background: "rgba(255,255,255,0.9)",
    padding: 12,
    boxShadow: "0 14px 34px rgba(2,6,23,0.06)",
  },
  walletTop: { display: "flex", alignItems: "center", gap: 8, marginBottom: 10 },
  walletDot: { width: 10, height: 10, borderRadius: 999, background: "rgba(34,197,94,0.9)" },
  walletTitle: { fontWeight: 1000, fontSize: 12, opacity: 0.75, letterSpacing: 0.2 },
  walletGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 },
  walletStat: {
    borderRadius: 14,
    border: "1px solid rgba(2,6,23,0.08)",
    background: "rgba(255,255,255,0.75)",
    padding: 10,
  },
  walletLabel: { fontSize: 11, fontWeight: 900, opacity: 0.6 },
  walletValue: { fontSize: 14, fontWeight: 1000, marginTop: 4 },

  grid: { display: "grid", gridTemplateColumns: "1.2fr 0.8fr", gap: 14, alignItems: "start" },

  card: {
    borderRadius: 22,
    border: "1px solid rgba(2,6,23,0.08)",
    background: "linear-gradient(180deg, rgba(255,255,255,0.92), rgba(255,255,255,0.78))",
    boxShadow: "0 18px 45px rgba(2,6,23,0.08)",
    padding: 16,
    display: "grid",
    gap: 12,
    minWidth: 0,
  },

  errBox: {
    borderRadius: 16,
    border: "1px solid rgba(239,68,68,0.22)",
    background: "rgba(239,68,68,0.10)",
    padding: 12,
    fontWeight: 900,
    color: "#b91c1c",
    fontSize: 13,
  },

  block: { display: "grid", gap: 8 },
  label: { fontWeight: 1000, opacity: 0.75, fontSize: 12 },

  field: {
    borderRadius: 16,
    border: "1px solid rgba(2,6,23,0.10)",
    background: "rgba(255,255,255,0.92)",
    boxShadow: "0 10px 22px rgba(2,6,23,0.04)",
    overflow: "hidden",
  },
  input: { width: "100%", border: "none", background: "transparent", padding: 12, fontWeight: 900, outline: "none" },
  select: { width: "100%", border: "none", background: "transparent", padding: 12, fontWeight: 900, outline: "none" },

  segment: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 },
  segBtn: {
    borderRadius: 16,
    border: "1px solid rgba(2,6,23,0.10)",
    background: "rgba(255,255,255,0.85)",
    padding: "12px 12px",
    cursor: "pointer",
    fontWeight: 1000,
    boxShadow: "0 12px 26px rgba(2,6,23,0.05)",
  },
  segOn: { border: "1px solid rgba(2,6,23,0.20)", transform: "translateY(-1px)" },

  summary: {
    borderRadius: 18,
    border: "1px solid rgba(2,6,23,0.08)",
    background: "rgba(2,6,23,0.03)",
    padding: 12,
    display: "grid",
    gap: 8,
  },
  sumRow: { display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" },
  sumKey: { opacity: 0.7, fontWeight: 1000, fontSize: 12 },
  sumVal: { fontWeight: 1000, fontSize: 13 },

  cta: {
    width: "100%",
    borderRadius: 18,
    border: "1px solid rgba(2,6,23,0.10)",
    padding: 12,
    fontWeight: 1000,
    cursor: "pointer",
    background: "white",
    boxShadow: "0 16px 36px rgba(2,6,23,0.10)",
  },
  ctaOff: { opacity: 0.7, cursor: "not-allowed" },

  note: { opacity: 0.7, fontWeight: 800, fontSize: 13, lineHeight: 1.3 },

  cardHead: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 },
  cardTitle: { fontWeight: 1000, fontSize: 14 },
  cardSub: { opacity: 0.7, fontWeight: 800, fontSize: 12, marginTop: 2 },

  empty: {
    borderRadius: 16,
    border: "1px dashed rgba(2,6,23,0.18)",
    background: "rgba(255,255,255,0.6)",
    padding: 14,
    fontWeight: 900,
    opacity: 0.75,
    fontSize: 13,
  },

  list: { display: "grid", gap: 10 },
  item: {
    borderRadius: 16,
    border: "1px solid rgba(2,6,23,0.08)",
    background: "rgba(255,255,255,0.85)",
    padding: 12,
    boxShadow: "0 10px 22px rgba(2,6,23,0.04)",
    display: "grid",
    gap: 8,
    minWidth: 0,
  },
  itemTop: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
  meta: { fontWeight: 900, fontSize: 12, opacity: 0.7, overflow: "hidden", textOverflow: "ellipsis" },
  itemBottom: { display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" },
  amount: { fontWeight: 1000, fontSize: 13 },
  date: { fontWeight: 900, fontSize: 12, opacity: 0.65 },

  statusPill: (st) => {
    const s = String(st || "").toUpperCase();
    const base = {
      padding: "6px 10px",
      borderRadius: 999,
      fontWeight: 1000,
      fontSize: 11,
      border: "1px solid rgba(2,6,23,0.12)",
      background: "rgba(2,6,23,0.03)",
      whiteSpace: "nowrap",
    };
    if (s === "PAID")
      return { ...base, border: "1px solid rgba(34,197,94,0.26)", background: "rgba(34,197,94,0.12)" };
    if (s === "REJECTED" || s === "FAILED")
      return { ...base, border: "1px solid rgba(239,68,68,0.26)", background: "rgba(239,68,68,0.12)" };
    if (s === "PROCESSING")
      return { ...base, border: "1px solid rgba(59,130,246,0.26)", background: "rgba(59,130,246,0.12)" };
    return { ...base, border: "1px solid rgba(234,179,8,0.28)", background: "rgba(234,179,8,0.14)" };
  },
};

/* ✅ Responsive tweak */
if (typeof window !== "undefined") {
  const mq = window.matchMedia?.("(max-width: 860px)");
  if (mq && mq.matches) S.grid.gridTemplateColumns = "1fr";
}
